import { createContext, useContext, useState, type ReactNode } from 'react';
import type { WarrantyListQuery } from '@econtractor/types';
import { useRegistryColumns } from 'utils/helpers/list/useRegistryColumns';

export type WarrantyColumn = 'category' | 'vendor' | 'id' | 'article' | 'price';

const columns: { id: WarrantyColumn; defaultVisible: boolean }[] = [
    'category',
    'vendor',
    'id',
    'article',
    'price',
].map((id) => ({ id: id as WarrantyColumn, defaultVisible: false }));

const useRegistryState = () => {
    const [query, setQuery] = useState<WarrantyListQuery>({
        order: 'prodName',
        desc: false,
        archived: false,
    });
    const [cardId, setCardId] = useState<number | null>(null);
    const [revision, setRevision] = useState(0);
    const { visible, toggle } = useRegistryColumns('registryColumns:warranties', columns);
    return {
        query,
        setQuery,
        cardId,
        setCardId,
        revision,
        refresh: () => setRevision((n) => n + 1),
        visible,
        toggle,
    };
};

const WarrantyContext = createContext<ReturnType<typeof useRegistryState> | null>(null);

interface WarrantyProviderProps {
    children: ReactNode;
}

export const WarrantyProvider = ({ children }: WarrantyProviderProps) => {
    const value = useRegistryState();
    return <WarrantyContext.Provider value={value}>{children}</WarrantyContext.Provider>;
};

export const useWarrantyRegistry = () => {
    const value = useContext(WarrantyContext);
    if (!value) throw new Error('WarrantyProvider is required');
    return value;
};
